// derivePoGrnState aggregates GRN state for a PO's GRN overview card.
//
// Two inputs, two sources of truth:
//   - invoices[].grns       → ALLOCATED GRN (invoice_grn_mapping). Drives the
//                             matched / mismatch states.
//   - receivedGrns          → RECEIVED GRN (grn_item, PO-level `received_grns`),
//                             independent of allocation. Used only as a fallback
//                             when nothing is allocated, so a partial/pending GRN
//                             (below the invoice value, so it never allocates and
//                             the invoice stays GRN_PENDING) is shown instead of 0.
//
// States:
//   - "absent"   → no GRN received at all.
//   - "pending"  → GRN received but not allocated/matched (partial/pending).
//   - "matched"  → allocated and every allocated invoice matched.
//   - "mismatch" → allocated but at least one allocated invoice did not match.
export function derivePoGrnState(invoices = [], receivedGrns = []) {
  const grnsById = new Map();
  invoices.forEach((inv) => {
    (inv.grns ?? []).forEach((alloc) => {
      const existing = grnsById.get(alloc.grn_id);
      if (existing) {
        existing.grn_value += alloc.allocated_value ?? 0;
      } else {
        grnsById.set(alloc.grn_id, {
          grn_id: alloc.grn_id,
          grn_date: alloc.created_at,
          grn_value: alloc.allocated_value ?? 0,
        });
      }
    });
  });
  const allocatedRows = Array.from(grnsById.values());
  const invoicesWithGrns = invoices.filter((inv) => (inv.grns ?? []).length > 0);

  // Nothing allocated: fall back to received GRN so partial/pending GRN is visible.
  if (invoicesWithGrns.length === 0) {
    if ((receivedGrns ?? []).length > 0) {
      const grnRows = receivedGrns.map((g) => ({
        grn_id: g.grn_id,
        grn_date: g.grn_date,
        grn_value: g.grn_value ?? 0,
      }));
      const totalGrnValue = grnRows.reduce((s, g) => s + (g.grn_value ?? 0), 0);
      return { grnRows, totalGrnValue, threeWayMatchState: "pending" };
    }
    return { grnRows: [], totalGrnValue: 0, threeWayMatchState: "absent" };
  }

  const totalGrnValue = allocatedRows.reduce((s, g) => s + (g.grn_value ?? 0), 0);
  const hasAnyMismatch = invoicesWithGrns.some(
    (inv) => inv.three_way_match_status?.match_status !== "SUCCESS"
  );
  return {
    grnRows: allocatedRows,
    totalGrnValue,
    threeWayMatchState: hasAnyMismatch ? "mismatch" : "matched",
  };
}
